/* eslint-disable max-len */
import http from '@/services/api/http';
import { getRedirectUrl } from '@/utils/helpers';
import sessionVars from '@/helpers/sessionVars';
import ls from '@/helpers/localStorage';
import constants from '@/constants';

export default {
  getManageInfo(returnUrl = '', generateState = 'false') {
    return http.get(`api/Account/ManageInfo?returnUrl=${returnUrl}&generateState=${generateState}`);
  },
  postToken(payload = {}) {
    return http.post('api/Account/token', payload);
  },
  getUserInfo() {
    const options = {};
    const token = ls.get('externalToken', null);
    if (token) {
      options.headers = {
        Authorization: `Bearer ${token}`,
      };
    }
    return http.get('api/Account/UserInfo', options);
  },
  getAccountDetail() {
    return http.get('api/Account/Detail');
  },
  postLogout(payload = {}) {
    const options = {
      withCredentials: true,
    };
    return http.post('api/Account/Logout', payload, options);
  },
  getStatus() {
    return http.get('api/Account/Status');
  },
  postRegister(payload) {
    const marketingPayload = sessionVars.payload(
      payload, 'PIReferral', 'utm_campaign', 'utm_source');
    return http.post('api/Account/Register', marketingPayload);
  },
  postRegisterExternal(payload) {
    const options = {};
    const token = ls.get('externalToken', null);
    if (token) {
      options.headers = {
        Authorization: `Bearer ${token}`,
      };
    }
    const marketingPayload = sessionVars.payload(
      payload, 'PIReferral', 'utm_campaign', 'utm_source');
    return http.post('api/Account/RegisterExternal', marketingPayload, options);
  },
  postResetPassword(payload) {
    return http.post('api/Account/ResetPassword', payload);
  },
  postForgotPassword(payload) {
    return http.post('api/Account/ForgotPassword', payload);
  },
  postChangePassword(payload) {
    return http.post('api/Account/ChangePassword', payload);
  },
  postSetPassword(payload) {
    return http.post('api/Account/SetPassword', payload);
  },
  postRemoveLogin(payload) {
    return http.post('api/Account/RemoveLogin', payload);
  },
  postAddExternalLogin(payload) {
    const options = {
      withCredentials: true,
    };
    return http.post('api/Account/AddExternalLogin', payload, options);
  },
  getExternalLogins(pathname = window.location.pathname, type = constants.SOCIAL_LOGIN_OR_REGISTER) {
    let redirect = pathname.replace(process.env.ROOT_PATH, '/');
    const accessToken = ls.get('token');

    if (accessToken !== null || redirect.includes('account/login') || redirect.includes('account/sign-up')) {
      redirect = '/';
    }

    const options = {
      params: {
        returnUrl: getRedirectUrl(`/account/social/auth/?redirect=${redirect}&type=${type}`),
        generateState: true,
      },
    };
    const token = ls.get('externalToken', null);
    if (token) {
      options.headers = {
        Authorization: `Bearer ${token}`,
      };
    }
    return http.get('/api/Account/ExternalLogins', options);
  },
  getVerifyID() {
    return http.get('api/Account/VerifyID');
  },
  postVerifyID(payload) {
    return http.post('api/Account/VerifyID', payload);
  },
  postUploadUserImage(payload) {
    const options = {
      headers: {
        'content-type': 'multipart/form-data',
      },
    };
    return http.post('api/Account/UploadUserImage', payload, options);
  },
  putResetUserImage() {
    return http.put('api/Account/ResetUserImage');
  },
  getCommunicationPreferences() {
    return http.get('api/Account/CommunicationPreferences');
  },
  postCommunicationPreferences(payload) {
    return http.post('api/Account/CommunicationPreferences', payload);
  },
  getCaptchaSiteKey() {
    // Public endpoint - must not send Authorization header (may have stale token)
    return http.get('api/Account/CaptchaSiteKey', {
      transformRequest: [(data, headers) => {
        delete headers.Authorization;
        return data;
      }],
    });
  },
  getCaptchaSiteKeyV3() {
    // Public endpoint - must not send Authorization header (may have stale token)
    return http.get('api/Account/CaptchaSiteKeyV3', {
      transformRequest: [(data, headers) => {
        delete headers.Authorization;
        return data;
      }],
    });
  },
  getDisqusLogin() {
    return http.get('api/Account/DisqusLogin');
  },
  getDisqusLogout() {
    return http.get('api/Account/DisqusLogout');
  },
  getDisqusUserAuth() {
    return http.get('api/Account/DisqusUserAuth');
  },
  getIdUploadUrl() {
    return http.get('api/Account/IdUploadUrl');
  },
  getTaxInfo({ reasonCode = '' }) {
    return http.get(`api/Account/TaxInfo?reasonCode=${reasonCode}`);
  },
  postTaxInfo(payload) {
    return http.post('api/Account/TaxInfo', payload);
  },
  putMessagingToken(token) {
    return http.put(`api/Account/MessagingToken?token=${token}`);
  },
  getChangeUsername() {
    return http.get('api/Account/ChangeUsername');
  },
  postChangeUsername(username) {
    return http.post(`api/Account/ChangeUsername?username=${username}`);
  },
  getDocumentUploadLink(source) {
    return http.get(`api/Account/DocumentUploadLink?source=${source}`);
  },
  putAgreedToTerms(payload) {
    return http.put('api/Account/UpdateAgreedToTerms', payload);
  },

  // --- Two-Factor Authentication ---

  // Login flow (anonymous - uses encrypted 2FA token)
  postTwoFactorSendCode(payload) {
    return http.post('api/Account/TwoFactor/SendCode', payload);
  },
  postTwoFactorVerify(payload) {
    return http.post('api/Account/TwoFactor/Verify', payload);
  },

  // User management (authenticated)
  getTwoFactorStatus() {
    return http.get('api/Account/TwoFactor/Status');
  },
  postTwoFactorSetupEmailSendCode() {
    return http.post('api/Account/TwoFactor/Setup/Email/SendCode');
  },
  postTwoFactorSetupEmailVerify(payload) {
    return http.post('api/Account/TwoFactor/Setup/Email/Verify', payload);
  },
  postTwoFactorSetupPhoneSendCode() {
    return http.post('api/Account/TwoFactor/Setup/Phone/SendCode');
  },
  postTwoFactorSetupPhoneVerify(payload) {
    return http.post('api/Account/TwoFactor/Setup/Phone/Verify', payload);
  },
  getTwoFactorSetupAuthenticator() {
    return http.get('api/Account/TwoFactor/Setup/Authenticator');
  },
  postTwoFactorSetupAuthenticatorVerify(payload) {
    return http.post('api/Account/TwoFactor/Setup/Authenticator/Verify', payload);
  },
  postTwoFactorEnable() {
    return http.post('api/Account/TwoFactor/Enable');
  },
  postTwoFactorDisable() {
    return http.post('api/Account/TwoFactor/Disable');
  },

  // --- Phone Management ---
  getPhone() {
    return http.get('api/Account/TwoFactor/Phone');
  },
  postPhoneUpdate(payload) {
    return http.post('api/Account/TwoFactor/Phone/Update', payload);
  },
  postPhoneVerify(payload) {
    return http.post('api/Account/TwoFactor/Phone/Verify', payload);
  },
};



// WEBPACK FOOTER //
// ./src/services/api/account.js